import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AppShell, Navbar as MantineNavbar, Header, Title, Stack, Group, UnstyledButton, Box } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useAuth } from '../features/auth/context/AuthContext';
import Navbar from '../features/auth/components/Navbar';
import LoginPage from '../features/auth/components/LoginPage';
import CourseGenerator from '../features/courseGen/components/CourseGenerator';
import CourseHistory from '../features/courseGen/components/CourseHistory';
import CourseView from '../features/courseGen/components/CourseView';

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// Public Route component (accessible only when not authenticated)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  if (user) {
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
};

const AppContent = () => {
  const { user } = useAuth();
  const [opened, setOpened] = useState(false);
  const [navbarCollapsed, setNavbarCollapsed] = useState(false);
  const location = useLocation();
  const courseState = location.state as { course: any } | null;

  return (
    <AppShell
      padding="md"
      navbar={user ? (
        <MantineNavbar
          p="md"
          hiddenBreakpoint="sm"
          hidden={!opened}
          width={{ sm: navbarCollapsed ? 80 : 300 }}
          sx={(theme) => ({
            transition: 'width 200ms ease',
            display: 'flex',
            flexDirection: 'column'
          })}
        >
          <Stack h="100%" spacing={0}>
            {/* Fixed Header */}
            <Box mb="md">
              {!navbarCollapsed ? (
                <Title order={3}>Your Skills</Title>
              ) : (
                <Title order={3} align="center">📚</Title>
              )}
            </Box>

            {/* Scrollable Content */}
            <Box 
              sx={{ 
                flexGrow: 1,
                overflowY: 'auto',
                marginRight: -8, // Compensate for scrollbar
                paddingRight: 8,
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#bbb',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#999',
                },
              }}
            >
              <CourseHistory collapsed={navbarCollapsed} />
            </Box>

            {/* Fixed Footer */}
            <Box mt="md">
              <UnstyledButton
                onClick={() => setNavbarCollapsed(c => !c)}
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: theme.spacing.xs,
                  borderRadius: theme.radius.sm,
                  color: theme.colors.gray[6],
                  '&:hover': {
                    backgroundColor: theme.colors.gray[0],
                  }
                })}
              >
                {navbarCollapsed ? <IconChevronRight size={20} /> : <IconChevronLeft size={20} />}
              </UnstyledButton>
            </Box>
          </Stack>
        </MantineNavbar>
      ) : undefined}
      header={
        <Header height={60}>
          <Navbar 
            burgerProps={{
              opened,
              onClick: () => setOpened(!opened),
              size: "sm"
            }}
          />
        </Header>
      }
    >
      <Routes>
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } 
        />
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <CourseGenerator />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/course/:courseId" 
          element={
            <ProtectedRoute>
              <CourseView course={courseState?.course} />
            </ProtectedRoute>
          } 
        />
      </Routes>
    </AppShell>
  );
};

const AppRouter = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default AppRouter; 